import React, { useState, useEffect, useRef } from "react";
import { LANDING } from "../data/siteData";
import ContactForm from "./ContactForm";

function WhyCarousel({ items, descriptions }) {
  const [active, setActive] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => setActive((p) => (p + 1) % items.length), 2500);
    return () => clearInterval(timer);
  }, [items.length]);

  return (
    <div className="why-two-col">
      <div className="why-titles">
        {items.map((item, i) => {
          const diff = ((i - active + items.length) % items.length);
          let cls = "why-title-item";
          if (diff === 0) cls += " wt-current";
          else if (diff === 1) cls += " wt-next";
          else if (diff === items.length - 1) cls += " wt-prev";
          else cls += " wt-hidden";
          return <div key={i} className={cls}>{item}</div>;
        })}
      </div>
      <div className="why-descs">
        {descriptions.map((desc, i) => (
          <div key={i} className={`why-desc-item${i === active ? ' active' : ''}`}>{desc}</div>
        ))}
      </div>
    </div>
  );
}

function TechCarousel({ tools }) {
  const [active, setActive] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => setActive((p) => (p + 1) % tools.length), 2000);
    return () => clearInterval(timer);
  }, [tools.length]);

  return (
    <div className="tech-carousel">
      {tools.map((t, i) => {
        const diff = ((i - active + tools.length) % tools.length);
        let cls = "tech-carousel-item";
        if (diff === 0) cls += " tc-center";
        else if (diff === 1) cls += " tc-right";
        else if (diff === tools.length - 1) cls += " tc-left";
        else cls += " tc-hidden";
        return (
          <div key={i} className={cls}>
            <img src={t.image} alt={t.name} />
            <span>{t.name}</span>
          </div>
        );
      })}
    </div>
  );
}

function ScrollReveal({ children, delay = 0 }) {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => setIsVisible(true), delay);
        }
      },
      { threshold: 0.1 }
    );

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [delay]);

  return (
    <div
      ref={ref}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(40px)',
        transition: 'opacity 0.8s ease, transform 0.8s ease'
      }}
    >
      {children}
    </div>
  );
}

function TeamCarousel({ members }) {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const len = members.length;

  useEffect(() => {
    if (paused) return;
    const timer = setInterval(() => setActive((p) => (p + 1) % len), 4000);
    return () => clearInterval(timer);
  }, [len, paused]);

  const goPrev = () => setActive((p) => (p - 1 + len) % len);
  const goNext = () => setActive((p) => (p + 1) % len);

  return (
    <div
      className="team-carousel-wrap"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <button className="team-carousel-arrow team-arrow-left" onClick={goPrev} aria-label="Previous">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M15 18l-6-6 6-6" />
        </svg>
      </button>
      <div className="team-carousel">
        {members.map((m, i) => {
          const diff = ((i - active + len) % len);
          let cls = "team-carousel-item";
          if (diff === 0) cls += " tc-center";
          else if (diff === 1) cls += " tc-right";
          else if (diff === len - 1) cls += " tc-left";
          else cls += " tc-hidden";
          return (
            <div key={i} className={cls}>
              <div className="team-carousel-img">
                <img src={m.image} alt={m.name} />
              </div>
              <h4 className="team-carousel-name">{m.name}</h4>
              <span className="team-carousel-role">{m.role}</span>
              <p className="team-carousel-desc">{m.desc}</p>
            </div>
          );
        })}
      </div>
      <button className="team-carousel-arrow team-arrow-right" onClick={goNext} aria-label="Next">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M9 18l6-6-6-6" />
        </svg>
      </button>
    </div>
  );
}

export default function LandingPage({ onNavigate }) {
  const d = LANDING;
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePos({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <>
      {/* ── HERO BANNER ── */}
      <section className="landing-hero">
        <div className="landing-hero-bg">
          <video
            className="landing-hero-video"
            autoPlay
            muted
            loop
            playsInline
            poster={d.hero.bgImage}
          >
            <source src={d.hero.bgVideo} type="video/mp4" />
          </video>
          <div className="landing-hero-overlay" />
          <div
            className="landing-hero-glow-cursor"
            style={{ left: `${mousePos.x}px`, top: `${mousePos.y}px` }}
          />
        </div>

        {/* Inner wrapper — fills 100vh, flex column, evenly spaced */}
        <div className="landing-hero-inner">
          {/* Badge */}
          <div className="landing-hero-badge">
            <span className="badge-dot"></span>
            <span>Engineering the Future of Your Business</span>
          </div>

          {/* Main text */}
          <div className="landing-hero-text-block">
            <h1 className="landing-hero-title" data-text="WE RAISE">WE RAISE</h1>
            <div className="landing-hero-you">
              <span>Y</span>
              <svg className="landing-hero-toggle" viewBox="0 0 180 84" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="4" y="4" width="172" height="76" rx="38" stroke="#c9d1d9" strokeWidth="8" />
                <circle cx="126" cy="42" r="28" fill="#c9d1d9" />
              </svg>
              <span>U</span>
            </div>
          </div>

          {/* Subtitle */}
          <p className="landing-hero-sub">{d.hero.subtitle}</p>

          {/* CTA */}
          <div className="landing-hero-cta-group">
            <button className="btn-hero-primary" onClick={() => onNavigate("contact")}>
              <span>Get Started</span>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          {/* Scroll button */}
          <button className="landing-hero-scroll" onClick={() => {
            const next = document.querySelector('.landing-about');
            if (next) next.scrollIntoView({ behavior: 'smooth' });
          }}>
            <span>SCROLL</span>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 5v14M19 12l-7 7-7-7" />
            </svg>
          </button>
        </div>

        {/* Marquee — pinned at very bottom */}
        <div className="landing-marquee">
          <div className="landing-marquee-track">
            <span>{d.hero.marquee}</span>
            <span>{d.hero.marquee}</span>
          </div>
        </div>
      </section>

      {/* ── ABOUT SECTION ── */}
      <section
        className="landing-about"
        style={{
          backgroundImage: 'linear-gradient(rgba(10,14,26,0.75) 0%, rgba(10,14,26,0.85) 100%), url(/assets/about-bg.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="landing-about-inner">
          <ScrollReveal>
            <h2 className="landing-about-heading">
              {d.about.heading.join(' ')}
            </h2>
          </ScrollReveal>
          <ScrollReveal delay={200}>
            <p className="landing-about-desc">{d.about.description}</p>
          </ScrollReveal>
          <ScrollReveal delay={400}>
            <button className="btn-know-more" onClick={() => onNavigate("about")}>
              <span className="btn-text-default">Know More</span>
              <span className="btn-text-hover">Get Started</span>
            </button>
          </ScrollReveal>
        </div>
      </section>

      {/* ── SERVICES SECTION ── */}
      <section className="landing-services" style={{
        backgroundImage: `linear-gradient(rgba(9, 10, 56, 0.96) 0%, rgba(10,14,26,0.55) 100%), url(${d.serviceFeature.image})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}>
        <div className="landing-services-container">
          <ScrollReveal>
            <h2 className="landing-services-title">{d.whatWeDo.title}</h2>
          </ScrollReveal>
          <ScrollReveal delay={200}>
            <div className="landing-services-features">
              {d.serviceFeature.features.map((feature, i) => (
                <p key={i} className="landing-services-feature-item">
                  <span className="feature-tick">&#10003;</span>{feature}
                </p>
              ))}
            </div>
          </ScrollReveal>
          <ScrollReveal delay={300}>
            <div className="landing-services-cta-center">
              <button className="btn-know-more" onClick={() => onNavigate("software")}>
                <span className="btn-text-default">Explore Services</span>
                <span className="btn-text-hover">Get Started</span>
              </button>
            </div>
          </ScrollReveal>
        </div>
      </section>
      {/* ── RECENT PROJECTS ── */}
      <section className="landing-projects" style={{ display: "none" }}>
        <div className="landing-projects-overlay" />
        <ScrollReveal>
          <div className="landing-projects-header">
            <div className="section-label">Portfolio</div>
            <h2 className="landing-projects-title">Recent Projects</h2>
            <p className="landing-projects-subtitle">Transforming ideas into digital excellence</p>
          </div>
        </ScrollReveal>
        <div className="landing-projects-grid">
          {d.recentProjects.projects.map((p, i) => (
            <ScrollReveal key={i} delay={i * 100}>
              <div className="lp-card" onClick={() => onNavigate("portfolio")}>
                <div className="lp-card-img">
                  <img src={p.image} alt={p.title} />
                  <div className="lp-card-overlay">
                    <span className="lp-card-view">View Project</span>
                  </div>
                </div>
                <div className="lp-card-info">
                  <div className="lp-card-text">
                    <h4>{p.title}</h4>
                    <p>{p.desc}</p>
                  </div>
                  <div className="lp-card-arrow">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#c9d1d9" strokeWidth="1.5">
                      <path d="M7 17L17 7M17 7H9M17 7V15" />
                    </svg>
                  </div>
                </div>
              </div>
            </ScrollReveal>
          ))}
        </div>
        <ScrollReveal delay={400}>
          <div className="landing-projects-cta">
            <button className="btn-know-more" onClick={() => onNavigate("portfolio")}>
              <span className="btn-text-default">Explore More</span>
              <span className="btn-text-hover">Get Started</span>
            </button>
          </div>
        </ScrollReveal>
      </section>

      {/* ── WHY CHOOSE US ── */}
      <section className="landing-why">
        <div className="landing-why-overlay" />
        <div className="landing-why-container">
          <ScrollReveal>
            <div className="landing-why-header">
              <h2 className="landing-why-title">{d.whyChoose.title}</h2>
            </div>
          </ScrollReveal>
          <ScrollReveal delay={400}>
            <div className="landing-why-content">
              <WhyCarousel items={d.whyChoose.rotatingItems} descriptions={d.whyChoose.description} />
            </div>
          </ScrollReveal>
          <ScrollReveal delay={600}>
            <div className="landing-why-cta">
              <button className="btn-know-more" onClick={() => onNavigate("about")}>
                <span className="btn-text-default">Explore More</span>
                <span className="btn-text-hover">Get Started</span>
              </button>
            </div>
          </ScrollReveal>
        </div>
      </section>
      {/* ── TECH ECOSYSTEM ── */}
      <section className="landing-tech">
        <div className="landing-tech-overlay" />
        <div className="landing-tech-container">
          <ScrollReveal>
            <div className="landing-tech-header">
              <h2 className="landing-tech-title">{d.techEcosystem.title}</h2>
            </div>
          </ScrollReveal>
          <ScrollReveal delay={200}>
            <div className="landing-tech-icons-wrap">
              {/* <img src="/assets/tech-glow.png" alt="" className="landing-tech-glow" /> */}
              <TechCarousel tools={d.techEcosystem.tools} />
              <img src="/assets/tech-line.png" alt="" className="landing-tech-line" />
            </div>
          </ScrollReveal>
          <ScrollReveal delay={400}>
            <p className="landing-tech-desc">{d.techEcosystem.description}</p>
          </ScrollReveal>
        </div>
      </section>

      {/* ── TEAM ── */}
      <section className="landing-team" style={{ display: "none" }}>
        <div className="landing-team-container">
          <ScrollReveal>
            <div className="landing-team-header">
              <div className="section-label">Our Team</div>
              <h2 className="landing-team-title">{d.team.title}</h2>
              <p className="landing-team-subtitle">Meet the experts driving innovation</p>
            </div>
          </ScrollReveal>
          <ScrollReveal delay={200}>
            <TeamCarousel members={d.team.members} />
          </ScrollReveal>
        </div>
      </section>

      {/* ── CONTACT FORM ── */}
      <ContactForm />
    </>
  );
}
